import { LINE_DISPENSE_STATES } from '@wodi/domain';
import { z } from 'zod';

import { amountSchema } from './money.ts';
import { batchChoiceSchema, batchPlanSchema } from './pharmacy.ts';
import { shaApprovalSchema } from './sha-approval.ts';

// Outpatient dispensing (docs/plans/phase1-dispensing.md §5.3). Prescriptions, dispenses
// and lines are addressed by UUID; no patient data travels in a URL or query string.

const quantitySchema = z.number().int().min(1).max(100_000);
const reasonSchema = z.string().trim().min(3, 'Say why, in a few words').max(500);

export const CLOSURE_REASONS = [
  'patient_declined',
  'bought_elsewhere',
  'out_of_stock',
  'prescriber_changed',
  'other',
] as const;

export const acknowledgementSchema = z.object({
  ruleId: z.string().min(1).max(80),
  reason: reasonSchema,
});

export const allergyFindingSchema = z.object({
  ruleId: z.string(),
  tier: z.enum(['blocking', 'interruptive', 'passive']),
  evidence: z.array(z.string()),
});

export const dispenseGateSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('open') }),
  z.object({ state: z.literal('approval_declined'), reason: z.string().nullable() }),
  z.object({ state: z.literal('waiting_for_approval') }),
  z.object({ state: z.literal('waiting_for_payment'), owed: amountSchema }),
  z.object({ state: z.literal('not_priced'), reason: z.string() }),
]);
export type DispenseGateView = z.infer<typeof dispenseGateSchema>;

export const preparedLineSchema = z.object({
  id: z.uuid(),
  dispenseId: z.uuid(),
  dispenseNumber: z.string(),
  serviceItemId: z.uuid(),
  itemName: z.string(),
  quantity: z.number().int(),
  status: z.enum(['prepared', 'dispensed', 'cancelled']),
  isControlled: z.boolean(),
  substituted: z.boolean(),
  instructions: z.string(),
  pharmacistNote: z.string().nullable(),
  preparedBy: z.string(),
  dispensedAt: z.iso.datetime().nullable(),
  gate: dispenseGateSchema.nullable(),
});
export type PreparedLineView = z.infer<typeof preparedLineSchema>;

export const workLineSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  status: z.enum(['active', 'stopped', 'cancelled', 'entered_in_error']),
  medicationDisplay: z.string(),
  serviceItemId: z.uuid(),
  itemName: z.string(),
  baseUnitDisplay: z.string(),
  isControlled: z.boolean(),
  substitutionAllowed: z.boolean(),
  /** The label text built from the prescription, or null when it must be typed. */
  instructions: z.string().nullable(),
  instructionsProblem: z.string().nullable(),
  prescribed: z.number().int(),
  dispensed: z.number().int(),
  prepared: z.number().int(),
  closed: z.number().int(),
  remaining: z.number().int(),
  state: z.enum(LINE_DISPENSE_STATES),
  usable: z.number().int(),
  preparedForOthers: z.number().int(),
  suggested: z.number().int(),
  shortBy: z.number().int(),
  warning: z.enum(['short', 'out_of_stock', 'prepared_for_others']).nullable(),
  /** The FEFO batches for the suggested quantity. */
  batches: z.array(batchPlanSchema),
  unitPrice: amountSchema.nullable(),
  unpricedReason: z.string().nullable(),
  /** The SHA approval gate for this prescription line on an SHA visit; null otherwise. */
  shaApproval: shaApprovalSchema.nullable(),
  lines: z.array(preparedLineSchema),
});
export type WorkLineView = z.infer<typeof workLineSchema>;

export const dispensingViewSchema = z.object({
  prescriptionId: z.uuid(),
  patientId: z.uuid(),
  visitId: z.uuid(),
  prescriberDisplay: z.string(),
  signedAt: z.iso.datetime(),
  status: z.enum(['active', 'cancelled', 'entered_in_error', 'completed']),
  allergyFindings: z.array(allergyFindingSchema),
  lines: z.array(workLineSchema),
});
export type DispensingView = z.infer<typeof dispensingViewSchema>;

export const QUEUE_STATES = [
  'new',
  'being_prepared',
  'waiting_for_approval',
  'waiting_for_payment',
  'ready_to_dispense',
  'part_dispensed',
] as const;

export const queueItemSchema = z.object({
  prescriptionId: z.uuid(),
  patient: z.object({
    id: z.uuid(),
    displayName: z.string(),
    patientNumber: z.string(),
  }),
  prescriberDisplay: z.string(),
  signedAt: z.iso.datetime(),
  lineCount: z.number().int(),
  state: z.enum(QUEUE_STATES),
  preparedBy: z.string().nullable(),
});
export type PharmacyQueueItem = z.infer<typeof queueItemSchema>;

export const pharmacyQueueSchema = z.object({
  /** False when no prescription source is connected; the screen says so. */
  connected: z.boolean(),
  items: z.array(queueItemSchema),
});

export const dispenseLineInputSchema = z.object({
  prescriptionLineId: z.uuid(),
  prescriptionLineVersion: z.number().int().min(1),
  /** What is given: the prescribed item, or a substitute with a reason. */
  serviceItemId: z.uuid(),
  quantity: quantitySchema,
  batches: z.array(batchChoiceSchema).min(1).max(20).optional(),
  overrideReason: reasonSchema.optional(),
  substitutionReason: reasonSchema.optional(),
  /** Typed label instructions, when none can be built from the prescription. */
  instructions: z.string().trim().min(3).max(500).optional(),
  pharmacistNote: z.string().trim().min(1).max(200).optional(),
  controlledRegisterReference: z.string().trim().min(1).max(80).optional(),
});

export const createDispenseBodySchema = z.object({
  clientRequestId: z.uuid(),
  prescriptionId: z.uuid(),
  storeId: z.uuid(),
  /** Hand over at once the lines whose gate is open; the rest stay prepared. */
  dispenseNow: z.boolean(),
  lines: z.array(dispenseLineInputSchema).min(1).max(50),
  acknowledgements: z.array(acknowledgementSchema).max(20).default([]),
});
export type CreateDispenseBody = z.infer<typeof createDispenseBodySchema>;

export const dispenseBodySchema = z.object({
  expectedVersion: z.number().int().min(1),
  lineIds: z.array(z.uuid()).min(1).max(50),
  controlledRegisterReferences: z
    .array(z.object({ lineId: z.uuid(), reference: z.string().trim().min(1).max(80) }))
    .max(50)
    .default([]),
  acknowledgements: z.array(acknowledgementSchema).max(20).default([]),
});

export const cancelDispenseBodySchema = z.object({
  expectedVersion: z.number().int().min(1),
  lineIds: z.array(z.uuid()).min(1).max(50).optional(),
  reason: reasonSchema,
});

export const dispenseViewSchema = z.object({
  id: z.uuid(),
  dispenseNumber: z.string(),
  prescriptionId: z.uuid(),
  patientId: z.uuid(),
  visitId: z.uuid(),
  storeId: z.uuid(),
  status: z.enum(['open', 'completed', 'cancelled']),
  version: z.number().int(),
  preparedAt: z.iso.datetime(),
  lines: z.array(
    preparedLineSchema.extend({
      batches: z.array(batchPlanSchema),
      controlledRegisterReference: z.string().nullable(),
    }),
  ),
  counsellingNotes: z.array(
    z.object({
      id: z.uuid(),
      note: z.string(),
      status: z.enum(['active', 'entered_in_error']),
      statusReason: z.string().nullable(),
      recordedBy: z.string(),
      recordedAt: z.iso.datetime(),
    }),
  ),
});
export type DispenseView = z.infer<typeof dispenseViewSchema>;

export const closeBalanceBodySchema = z.object({
  prescriptionLineVersion: z.number().int().min(1),
  /** Default: everything still owed. */
  quantity: quantitySchema.optional(),
  reasonCode: z.enum(CLOSURE_REASONS),
  note: z.string().trim().max(500).optional(),
});

export const counsellingNoteBodySchema = z.object({ note: z.string().trim().min(3).max(2000) });
export const enteredInErrorBodySchema = z.object({ reason: reasonSchema });

export const labelsViewSchema = z.object({
  labels: z.array(
    z.object({
      lineId: z.uuid(),
      paper: z.enum(['label-50x25', 'receipt-80mm']),
      facilityLine: z.string(),
      patientName: z.string(),
      patientNumber: z.string(),
      medicine: z.string(),
      quantityLine: z.string(),
      instructions: z.string(),
      pharmacistNote: z.string().nullable(),
      dispensedOn: z.string(),
      dispenseNumber: z.string(),
      /** 0 before the first print; above 0 prints "Copy". */
      printCount: z.number().int().min(0),
    }),
  ),
});
export type LabelsView = z.infer<typeof labelsViewSchema>;

export const labelsPrintedBodySchema = z.object({ lineIds: z.array(z.uuid()).min(1).max(50) });
